import {useState} from "react";
import {App as AntdApp, Col, Row, Table, Tag, Tooltip} from "antd";
import dayjs from "dayjs";
import {FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetExtraPacksQuery,
	useGetMyExtraPurchasesQuery,
	useRequestExtraPurchaseMutation,
} from "~/reduxs/api/subscriptionApiSlice";
import ExtraPurchaseModal from "./ExtraPurchaseModal";
import style from "../style/Subscription.module.scss";
import className from "classnames/bind";

const cn = className.bind(style);

const nf = (n) => Number(n || 0).toLocaleString('vi-VN');

const STATUS_TAG = {
	pending: {color: 'gold', label: 'Chờ duyệt'},
	approved: {color: 'green', label: 'Đã duyệt'},
	rejected: {color: 'red', label: 'Từ chối'},
};

/** Cột bảng lịch sử đơn mua Extra của chính user. */
const HISTORY_COLUMNS = [
	{
		title: 'Thời gian', dataIndex: 'created', key: 'created', width: 150, responsive: ['md'],
		render: (v) => (v ? dayjs(v).format('HH:mm DD/MM/YYYY') : '—'),
	},
	{
		title: 'Gói Extra', key: 'pack',
		render: (_, r) => (
			<span>
				<b>{r.pack_name}</b>
				<span style={{color: '#6b7280'}}> · +{nf(r.units)} bài</span>
			</span>
		),
	},
	{
		title: 'Giá', dataIndex: 'price', key: 'price', align: 'right', width: 110,
		render: (v) => (v > 0 ? `${nf(v)}đ` : 'Tặng'),
	},
	{
		title: 'Trạng thái', key: 'status', width: 130,
		render: (_, r) => {
			const m = STATUS_TAG[r.status] || {color: 'default', label: r.status};
			return (
				<span>
					<Tag color={m.color}>{m.label}</Tag>
					{r.admin_note && (
						<Tooltip title={`Phản hồi: ${r.admin_note}`}>
							<FontAwesomeIcon icon="fa-light fa-message-lines" style={{color: '#9ca3af', cursor: 'help'}} />
						</Tooltip>
					)}
				</span>
			);
		},
	},
];

/**
 * Section "Gói Extra" ở trang Gói & Hạn mức — mua thêm bài viết AI tự động khi hết
 * quota tháng mà không phải nâng gói. Đặt giữa lưới thẻ giá và bảng so sánh.
 * Ẩn khi chưa có pack (chưa migrate/admin tắt hết) hoặc tài khoản root.
 */
function ExtraPackSection({sub}) {
	const {notification} = AntdApp.useApp();
	const {data: packs = []} = useGetExtraPacksQuery();
	const {data: purchases} = useGetMyExtraPurchasesQuery();
	const [requestExtra, {isLoading: sending}] = useRequestExtraPurchaseMutation();

	// Pack đang chọn để gửi đơn mua (null = đóng modal).
	const [buyPack, setBuyPack] = useState(null);

	const isRoot = Boolean(sub?.is_root);
	const pending = purchases?.pending || null;
	const items = purchases?.items || [];
	const extra = sub?.auto_content_posts?.extra || 0;
	const resetAt = sub?.auto_content_posts?.reset_at;

	if (isRoot || packs.length === 0) return null;

	const handleBuy = async ({note}) => {
		try {
			const res = await requestExtra({pack_id: buyPack.id, note}).unwrap();
			notification.success({message: 'Đã gửi yêu cầu mua', description: res?.message || 'Quản trị viên sẽ xác nhận thanh toán và cộng bài cho bạn.'});
			setBuyPack(null);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không gửi được yêu cầu mua.')});
		}
	};

	return (
		<div className={cn('section')}>
			<div className={cn('section-head')}>
				<h3 className={cn('section-title')}>Gói Extra — mua thêm bài viết AI</h3>
				<p className={cn('section-sub')}>
					Hết hạn mức bài viết AI tự động của chu kỳ? Mua thêm mà không cần nâng gói —
					bài Extra dùng đến <b>hết chu kỳ gói được cấp</b>, không cộng dồn sang chu kỳ sau.
				</p>
			</div>

			{extra > 0 && (
				<div className={cn('extra-granted')}>
					<FontAwesomeIcon icon="fa-light fa-circle-check" /> Chu kỳ này bạn được cộng thêm <b>{nf(extra)} bài Extra</b>
					{resetAt ? <> · hết hạn {dayjs(resetAt).format('DD/MM/YYYY')}</> : null}
				</div>
			)}

			<Row gutter={[18, 18]}>
				{packs.map((p) => {
					const isPendingPack = pending && pending.pack_id === p.id;
					const lockedByOther = pending && pending.pack_id !== p.id;

					return (
						<Col xs={24} sm={12} xl={8} key={p.id}>
							<div className={cn('extra-pack')}>
								<span className={cn('plan-name')}>{p.name}</span>
								<div className={cn('extra-units')}>
									+{nf(p.units)} <span>bài viết AI</span>
								</div>
								<div className={cn('plan-price')}>
									<span className={cn('plan-amount')}>{nf(p.price)}đ</span>
									<span className={cn('plan-cycle')}>· {nf(Math.round(p.price / (p.units || 1)))}đ/bài</span>
								</div>
								<div className={cn('extra-desc')}>{p.description}</div>

								<div className={cn('plan-cta')}>
									{isPendingPack ? (
										<div className={cn('plan-btn', 'is-current')}>
											<FontAwesomeIcon icon="fa-light fa-hourglass-half" /> Đang chờ duyệt
										</div>
									) : lockedByOther ? (
										<Tooltip title="Bạn đã có một đơn mua đang chờ duyệt. Vui lòng đợi quản trị viên xử lý.">
											<div className={cn('plan-btn', 'is-disabled')}>Mua thêm</div>
										</Tooltip>
									) : !p.can_buy ? (
										<Tooltip title="Gói Extra bài viết AI chỉ dành cho gói Pro/Max — cộng thêm hạn mức, không mở khoá tính năng. Vui lòng nâng cấp gói.">
											<div className={cn('plan-btn', 'is-disabled')}>Mua thêm</div>
										</Tooltip>
									) : (
										<div
											className={cn('plan-btn')}
											role="button"
											tabIndex={0}
											onClick={() => setBuyPack(p)}
											onKeyDown={(e) => e.key === 'Enter' && setBuyPack(p)}
										>
											<FontAwesomeIcon icon="fa-light fa-cart-plus" /> Mua thêm
										</div>
									)}
								</div>
							</div>
						</Col>
					);
				})}
			</Row>

			{items.length > 0 && (
				<div className={cn('extra-history')}>
					<div className={cn('extra-history-title')}>Đơn mua Gói Extra của bạn</div>
					<Table
						columns={HISTORY_COLUMNS}
						dataSource={items}
						rowKey="id"
						size="small"
						scroll={{x: 'max-content'}}
						pagination={{pageSize: 5, hideOnSinglePage: true}}
					/>
				</div>
			)}

			<ExtraPurchaseModal
				open={Boolean(buyPack)}
				pack={buyPack}
				submitting={sending}
				onCancel={() => setBuyPack(null)}
				onSubmit={handleBuy}
			/>
		</div>
	);
}

export default ExtraPackSection;
